/* eslint-disable no-unused-vars */
class CommunityHub extends HTMLElement {
  constructor () {
    super()
    this.page = 1
    this.isLoading = false
    this.itemsPerPage = 12
    this.searchForm = this.querySelector('.community-hub__search-form')
    this.searchInput = this.querySelector('#community-search')
    this.clearButton = this.querySelector('.community-hub__search-input-clear')
    this.communitiesGrid = this.querySelector('.community-hub__communities')
    this.communityItems = this.querySelectorAll('.community-hub__community')
    this.noResults = this.querySelector('.community-hub__no-results')
    this.loadMoreButton = this.querySelector('[data-load-more]')
    this.loadMoreContainer = this.querySelector('.community-hub__load-more')

    // Store original communities for reset
    this.originalCommunities = Array.from(this.communityItems)

    // Add minlength attribute to input
    this.searchInput.setAttribute('minlength', '2')
    this.searchInput.setAttribute('pattern', '.{2,}')
    this.searchInput.setAttribute('title', 'Please enter at least 2 characters')
  }

  formatMemberCount (count) {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1).replace('.0', '')}k`
    }
    return count.toString()
  }

  sortCommunitiesByMemberCount (items) {
    const sorted = Array.from(items).sort((a, b) => {
      const countA = parseFloat(a.dataset.memberCount)
      const countB = parseFloat(b.dataset.memberCount)
      return countB - countA
    })
    return sorted
  }

  updateMemberCountDisplay () {
    this.communityItems.forEach(item => {
      const memberCountElement = item.querySelector('.community-hub__community-member-count')
      if (memberCountElement) {
        const count = parseFloat(item.dataset.memberCount)
        const formattedCount = this.formatMemberCount(count)
        const translation = this.dataset.memberCountTranslation
        memberCountElement.textContent = translation.replace('{{ count }}', formattedCount)
      }
    })
  }

  connectedCallback () {
    this.setupInitialDisplay()
    this.addEventListeners()
    this.initializeFuzzySearch()
  }

  setupInitialDisplay () {
    const sortedItems = this.sortCommunitiesByMemberCount(this.communityItems)
    this.communitiesGrid.innerHTML = ''
    sortedItems.forEach(item => this.communitiesGrid.appendChild(item))

    this.communityItems = this.querySelectorAll('.community-hub__community')
    this.updateMemberCountDisplay()

    this.communityItems.forEach((item, index) => {
      if (index >= this.itemsPerPage) {
        item.classList.add('hidden')
      }
    })

    if (this.communityItems.length > this.itemsPerPage) {
      this.loadMoreContainer.classList.remove('hidden')
      this.loadMoreContainer.classList.add('flex')
    }
  }

  addEventListeners () {
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', this.handleSearch.bind(this))
    }

    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', this.handleLoadMore.bind(this))
    }

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        this.toggleClearButton()
      })
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.resetSearch()
      })
    }
  }

  initializeFuzzySearch () {
    const fuseScriptUrl = 'https://cdn.jsdelivr.net/npm/fuse.js@7.0.0/dist/fuse.min.js'

    const initFuse = () => {
      const communities = Array.from(this.communityItems).map(item => ({
        element: item,
        title: item.querySelector('.community-hub__community-title')?.textContent || '',
        description: item.querySelector('.community-hub__community-description')?.textContent || '',
      }))

      this.fuse = new window.Fuse(communities, {
        keys: [
          { name: 'title', weight: 2 },
          { name: 'description', weight: 1 },
        ],
        threshold: 0.4,
        ignoreLocation: true,
        distance: 500,
      })
    }

    window.Tentbox.loadScript(fuseScriptUrl, initFuse)
  }

  toggleClearButton () {
    if (!this.clearButton) return

    if (this.searchInput.value) {
      this.clearButton.classList.remove('hidden')
      this.clearButton.classList.add('flex')
    } else {
      this.clearButton.classList.add('hidden')
      this.clearButton.classList.remove('flex')
    }
  }

  resetSearch () {
    // Clear input and update clear button state
    this.searchInput.value = ''
    this.toggleClearButton()

    // Reset page counter
    this.page = 1

    // Clear the grid
    this.communitiesGrid.innerHTML = ''

    // Restore original communities
    const sortedItems = this.sortCommunitiesByMemberCount(this.originalCommunities)

    // Append all items and handle visibility
    sortedItems.forEach((item, index) => {
      item.classList.remove('hidden')
      if (index >= this.itemsPerPage) {
        item.classList.add('hidden')
      }
      this.communitiesGrid.appendChild(item)
    })

    // Update reference to community items
    this.communityItems = this.querySelectorAll('.community-hub__community')

    // Update member count display
    this.updateMemberCountDisplay()

    // Hide no results message
    if (this.noResults) {
      this.noResults.classList.add('hidden')
    }

    // Show/hide load more button
    if (this.loadMoreContainer) {
      if (this.originalCommunities.length > this.itemsPerPage) {
        this.loadMoreContainer.classList.remove('hidden')
        this.loadMoreContainer.classList.add('flex')
      } else {
        this.loadMoreContainer.classList.add('hidden')
        this.loadMoreContainer.classList.remove('flex')
      }
    }
  }

  async handleSearch (event) {
    event.preventDefault()
    if (this.isLoading) return

    const searchTerm = this.searchInput.value.toLowerCase().trim()

    if (searchTerm === '') {
      this.resetSearch()
      return
    }

    if (searchTerm.length < 2) {
      this.searchInput.classList.add('border-red-500', 'focus:border-red-500')
      this.searchInput.classList.remove('focus:border-black', 'hover:border-black')

      this.searchInput.reportValidity()

      setTimeout(() => {
        this.searchInput.classList.remove('border-red-500', 'focus:border-red-500')
        this.searchInput.classList.add('focus:border-black', 'hover:border-black')
      }, 2000)

      return
    }

    this.isLoading = true
    this.searchForm.setAttribute('loading', 'true')
    this.setAttribute('loading', '')

    await new Promise(resolve => setTimeout(resolve, 800))

    this.toggleClearButton()

    if (this.fuse) {
      const results = this.fuse.search(searchTerm)
      let matchCount = 0
      const matchedItems = []

      this.communityItems.forEach(item => {
        const isMatch = results.some(result => result.item.element === item)
        if (isMatch) {
          matchedItems.push(item)
          matchCount++
        }
      })

      const sortedMatches = this.sortCommunitiesByMemberCount(matchedItems)
      this.communitiesGrid.innerHTML = ''
      sortedMatches.forEach(item => this.communitiesGrid.appendChild(item))

      this.communityItems = this.querySelectorAll('.community-hub__community')
      this.updateMemberCountDisplay()

      this.communityItems.forEach((item, index) => {
        if (index >= this.itemsPerPage) {
          item.classList.add('hidden')
        } else {
          item.classList.remove('hidden')
        }
      })
    }

    const visibleItems = Array.from(this.communityItems).filter(item => !item.classList.contains('hidden'))
    this.noResults.classList.toggle('hidden', visibleItems.length > 0)

    this.loadMoreContainer.classList.toggle('hidden', visibleItems.length <= this.itemsPerPage)
    this.loadMoreContainer.classList.toggle('flex', visibleItems.length > this.itemsPerPage)

    this.page = 1
    this.isLoading = false
    this.searchForm.setAttribute('loading', 'false')
    this.removeAttribute('loading')
  }

  async handleLoadMore () {
    if (this.isLoading) return

    this.isLoading = true
    this.loadMoreButton.setAttribute('loading', '')

    const searchTerm = this.searchInput.value.toLowerCase().trim()
    const start = this.page * this.itemsPerPage
    const end = start + this.itemsPerPage
    let visibleCount = 0

    await new Promise(resolve => setTimeout(resolve, 250))

    this.communityItems.forEach(item => {
      const title = item.querySelector('.community-hub__community-title').textContent.toLowerCase()
      const isMatch = searchTerm === '' || title.includes(searchTerm)

      if (isMatch) {
        if (visibleCount >= start && visibleCount < end) {
          item.classList.remove('hidden')
        }
        visibleCount++
      }
    })

    this.page += 1

    // Hide load more if no more items to show
    if (visibleCount <= end) {
      this.loadMoreContainer.classList.remove('flex')
      this.loadMoreContainer.classList.add('hidden')
    }

    this.isLoading = false
    this.loadMoreButton.removeAttribute('loading')
  }
}

if (!customElements.get('community-hub')) {
  customElements.define('community-hub', CommunityHub)
}
/* eslint-enable no-unused-vars */
